import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Description, Profile, Info, Video } from '../models/profile';
import { Observable } from 'rxjs';
import { HelperService } from './helper.service';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { map, take } from 'rxjs/operators';
import { Menu, MenuGroup, MenuGroupItem, MenuGroupItemWeight } from '../models/menu';
import { Event } from '../models/event';
import { Gallery } from '../models/gallery';
import { New } from '../models/new';
import { TranslationService } from './translation.service';
import { Category } from '../models/category';
import { Offer } from '../models/offer';
import { Comment } from '../models/comment';
import { DOCUMENT } from '@angular/common';
import {CITY} from "../tokens";

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  city: string;
  constructor(
    private translationService: TranslationService,
    private helperService: HelperService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(CITY) private injectedCity: string,
    private http: HttpClient) {
    this.city = this.injectedCity || this.helperService.getCity();
  }

  byId(id: string): Observable<Profile> {
    const language = this.translationService.language;

    const url = environment.API_URL + `profile/${this.city}/${id}/${language}/`;
    let menuGroups: MenuGroup[] = [];
    this.getMenuGroups(id).pipe(take(1)).subscribe(x => menuGroups = x);

    return this.http
      .get(url)
      .pipe(map(result => this.mapProfile(result, menuGroups, this.city)));
  }

  getMenuGroupItems(groupId: string, profileId: string): Observable<MenuGroupItem[]> {
    const params = new HttpParams()
      .set("city", this.city)
      .set("action", "getMenuItems")
      .set("zId", profileId)
      .set("gId", groupId);

    return this.http
      .get(environment.MOBIBLE_API_URL, { params: params })
      .pipe(map(result => this.mapMenuGroupItems(result)));
  }
  getMenuGroups(profileId: string): Observable<MenuGroup[]> {
    const params = new HttpParams()
      .set("city", this.city)
      .set("action", "getMenuGroups")
      .set("zId", profileId);

    return this.http
      .get(environment.MOBIBLE_API_URL, { params: params })
      .pipe(map(result => this.mapMenuGroups(result,profileId)));
  }
  submitPhoneRequest(venueId, phone) {
    const params = new HttpParams()

      .set("action", "mobilePhonesCalledStats")
      .set("city", "mobile")
      .set("phoneType", this.document.defaultView.matchMedia('(min-width: 768px)') ? "0" : "1")
      .set("zId", venueId)
      .set("phone", encodeURIComponent(phone))
      .set("zCity", this.helperService.getCity());

   return this.http
      .get(environment.MOBIBLE_API_URL, { params: params });
  }
  makeVote(vote, zId, userIp) {
    const params = new HttpParams()
      .set("setNote", "1")
      .set("city", this.city)
      .set("action", "voteForZavedenie")
      .set("uId", "0")
      .set("zId", zId)
      .set("vote", vote)
      .set("userIp", userIp)

    return this.http.get(environment.MOBIBLE_API_URL, { params: params });
  }
  private mapMenuGroupItems(menuGroupItemResult: any): MenuGroupItem[] {
    const response = menuGroupItemResult['response'];

    return response?.map(x => <MenuGroupItem>{
      id: Number(x['id']),
      venueId: Number(x['zavedenia_id']),
      menuGroupId: Number(x['menu_group_id']),
      name: x['name'],
      name_uk: x['name_uk'],
      filename: x['filename'],
      description: x['description'],
      description_uk: x['description_uk'],
      position: Number(x['position']),
      weights: x['weights']?.map(y => <MenuGroupItemWeight>{
        id: Number(y['id']),
        name: y['name'],
        grams: Number(y['gramaj']),
        price: Number(y['cena']),
        measure: Number(y['measure'])
      })
    });
  }

  private mapMenuGroups(menuGroupResult: any,profileId: string): MenuGroup[] {
    const response = menuGroupResult['response'];

    let result: MenuGroup[] = [];
    response.forEach(x => {
      let mapped = <MenuGroup>{
        id: Number(x['id']),
        name: x['name'],
        name_uk: x['name_uk'],
        type: x['type'],
        pdf: x['pdf']
      };

      this.getMenuGroupItems(x['id'],profileId)
        .pipe(take(1)).subscribe(c => mapped.items = c);

      result.push(mapped);
    });

    return result;
  }

  private mapProfile(result: any, menuGroups: MenuGroup[], city: string): Profile {

    const profile = result['profile'];
    const galleries = result['galleries'];
    const comments = result['comments'];
    const description = result['types'];
    const events = result['events'];
    const news = result['news'];
    const videos = result['video_links'];
    const offers = result['site_offers'];
    let venueTypeVenue = 12;
    let cousineVenue = 8;
    let appropriateForVenue = 7;
    let extras = 9;
    if(city == 'plovdiv'){
      venueTypeVenue = 1;
      cousineVenue = 2;
      appropriateForVenue = 5;
      extras = 4;
    }

    const backgroundFile = galleries.find(x => x !== undefined);
    let mapped: Profile = {
      approval: profile['approval'],
      id: Number(profile['id']),
      seo: profile['seo'],
      pathBig: `https://zavedenia.com/zimages/${city}/big/${Number(profile['id'])}/`,
      pathMedium: `https://zavedenia.com/zimages/${city}/medium/${Number(profile['id'])}/`,
      pathThumb: `https://zavedenia.com/zimages/${city}/thumb/${Number(profile['id'])}/`,
      image_location: `https://zavedenia.com/${profile['image_location']}`,
      profileFilename: profile['filename'],
      backgroundFilename: backgroundFile !== undefined ? backgroundFile['pictures'][0]['filename'] : undefined,
      info: <Info>{note: profile['note'] || '',
        name: profile['zavedenie'] || '',
        categories: (profile['types'] || [])?.map(x => <Category>{
          name: x['tag_name'] || '',
          link_category: x['tag_url'] || ''
        }),
        address: profile['address'] || '',
        open: Boolean(profile['open']),
        reservationsAllowed: Number(profile['reservations_allowed'] || 0),
        orderbg: profile['orderbg'] || '',
        stars: Number(profile['starts'] || 0),
        priceRangeText: profile['price_range_txt'] || '',
        visits: Number(profile['visits'] || 0),
        ups: Number(profile['up'] || 0),
        downs: Number((profile['all_votes'] || 0) - (profile['up'] || 0)),
        capacityIn: Number(profile['capacity_in'] || 0),
        capacityOut: Number(profile['capacity_out'] || 0),
        workingHours: profile['working_hours'] || [],
        phone: profile['phone'] || '',
        website: profile['website'] || '',
        mapX: Number(profile['map_x'] || 0),
        mapY: Number(profile['map_y'] || 0),
        primary_type_id: Number(profile['primary_type_id'] || 0),
        primary_name: profile['primary_name'] || '',
        description: <Description>{
          aboutVenue: profile['description'] || '',
          appropriateFor: (description && description[appropriateForVenue]) ? description[appropriateForVenue].join(", ") : '',
          music: (description && description['10']) ? description['10'].join(", ") : '',
          venueType: (description && description[venueTypeVenue]) ? description[venueTypeVenue].join(", ") : '',
          cuisine: (description && description[cousineVenue]) ? description[cousineVenue].join(", ") : '',
          facilities: (description && description[extras]) ? description[extras].join(", ") : '',
        }
      },
      menuCount: profile["have_menu"],
      offersCount: profile["have_offers"],
      newsCount: profile["have_news"],
      eventsCount: profile["have_events"],
      commentsCount: profile["have_comments"],
      imagesCount: profile["have_photos"],
      offers: offers?.map(x => <Offer>{
        id: Number(x['id']),
        venueId: Number(x['zid']),
        description: x['description'],
        label: x['label'],
        startDate: moment(x['start_date']).locale('BG').format("LL"),
        endDate: moment(x['end_date']).locale('BG').format("LL"),
        phones: x['phone'],
        site: x['site'],
        filename: x['filename'],
        filename1: x['filename1'],
        filename2: x['filename2']
      }) || null,
      videos: videos?.map(x => <Video>{
        id: Number(x['id']),
        title: x['title'],
        type: x['type'],
        url: x['url'],
        venueId: Number(x['zavedenia_id'])
      }),
      comments: comments?.map(x => {
          return <Comment>{
          id: Number(x['zid']),
          comment: x['comment'],
          date: x['date_posted'],
          name: x['Name'],
          rating: x['rating']
        }
      }),
      menu: <Menu>{
        groups: menuGroups
      },
      galleries: galleries?.map(x => <Gallery>{
        id: Number(x['id']),
        name: x['name'],
        description: x['description'],
        position: Number(x['position']),
        pictures: x['pictures']?.map(v => v['filename'])
      }),
      events: events?.map(event => {
        const eventDate = event['event_date'] ? moment(event['event_date']).locale('BG').format("LL") : "";
        const startDate = event['start_date'] ? moment(event['start_date']).locale('BG').format("LL") : "";

        return <Event>{
          id: event['id'],
          venueId: event['zid'],
          venueName: event['zavedenie'],
          label: event['label'],
          description: event['description'],
          address: event['address'],
          mapX: Number(event['map_x']),
          maxY: Number(event['map_y']),
          eventDate: eventDate != 'Invalid date' ? eventDate : "",
          startDate: startDate,
          filename: event['filename'],
          endDate: event['end_date'],
          hours: event['hours'],
          entrance: Number(event['entrance']),
          distance: Number(event['distance']),
          phones: event['phone'],
          site: event['site'],
          day: event['day'],
        }
      }),
      news: news?.map(x => {
        return <New>{
          id: x['id'],
          label: x['label'],
          description: x['description'],
          dateCreated: moment(x['date_created']).locale('BG').format("LL"),
          author: x['author'],
          link: x['link'],
          filename: x['filename'],
          venueId: x['zid'],
          site: x['site'],
          venueName: '',
          linkedVenues: x['linkedVenues'],
          filenameNews: x['filenamenews'],
          phones: x['phone']
        }
      })
    };

    return mapped;
  }
}
